"use client";
import BackgroundPlayer from "next-video/background-player";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { checkIfImage } from "../../lib/utils";
import { LandingScreenPropsModel } from "./LandingScreen.model";
import Mask from "./Mask/Mask.component";
import StaticMask from "./StaticMask/StaticMask.component";

// Header text component
const HeaderText = ({ text }: { text: string }) => (
  <span className="font-outfit mobile:p-4 mobile:text-base p-8 text-2xl leading-none font-bold uppercase">
    {text}
  </span>
);

const LandingScreen = ({ asset, title }: LandingScreenPropsModel) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // This addresses the iOS Safari/Chrome issue where video pauses when switching apps
  useEffect(() => {
    // Define visibility change handler function
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        // Instead of using the ref, directly access the video element by ID
        // This ensures we're accessing the actual HTML video element
        const videoElement = videoRef.current;

        if (videoElement) {
          try {
            // Small timeout
            await new Promise(resolve => setTimeout(resolve, 50));
            const playPromise = videoElement.play();
            if (playPromise !== undefined) {
              playPromise.catch((error: Error) => {
                // Auto-play was prevented
                console.error("Play was prevented:", error);
                // On iOS, often needs user interaction
                // Could potentially show a play button here if needed
              });
            }
          } catch (error) {
            console.error("Error controlling video:", error);
          }
        } else {
          console.warn(
            "Could not find video element - BackgroundPlayer might be using a different structure",
          );
        }
      }
    };

    // Register the visibility change event listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Clean up event listener when component unmounts
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []); // Empty dependency array as we're using document.querySelector

  return (
    <div className="landing-screen bg-core-red relative" ref={containerRef}>
      {/* Background video */}
      {asset && !checkIfImage(asset?.contentType) && (
        <BackgroundPlayer
          id="background-video"
          ref={videoRef}
          src={asset.url}
          className="h-screen w-screen [&>.next-video-bg-video]:h-screen [&>.next-video-bg-video]:w-screen"
          controls={false}
          playsInline
          autoPlay
          muted
          loop
        />
      )}
      {asset && checkIfImage(asset?.contentType) && (
        <Image
          className="h-screen w-screen object-cover"
          src={asset.url}
          alt={asset?.title || "Landing Screen Image"}
          width={asset.width}
          height={asset.height}
        />
      )}

      {/* Header text */}
      <div className="absolute bottom-0 left-0 flex h-auto w-screen justify-between font-bold text-white">
        <HeaderText text="do" />
        <HeaderText text="things" />
      </div>

      {/* Video overlay mask */}
      <div className="absolute top-0 left-0 h-screen w-screen">
        {title ? <StaticMask title={title} /> : <Mask />}
      </div>
    </div>
  );
};

export default LandingScreen;
